import type { PropsWithChildren } from 'react';
import { Component } from 'react';

import { logger } from '~/lib/logger';

const getEnvironment = (url: string): string | null => {
  if (url.includes('staging.')) {
    return 'staging';
  }

  if (url.includes('dev.')) {
    return 'development';
  }

  if (url.includes('localhost')) {
    return 'local';
  }

  return null;
};

class ErrorBoundary<
  T extends PropsWithChildren<{
    fallback?: PropsWithChildren['children'];
    onError?: (error: Error) => void;
  }>,
> extends Component<T, { error?: string }> {
  static displayName = 'ErrorBoundary';
  constructor(props: T) {
    super(props);
    this.state = { error: undefined };
  }

  componentDidCatch(error: Error) {
    logger.error(error);
    this.props.onError?.(error);

    const environment = getEnvironment(window.location.hostname);

    const isNetworkError =
      // V1 dynamic import (preserve existing behavior)
      error.message.includes('Failed to fetch dynamically imported module') ||
      // V2 module-federation runtime manifest fetch failures (e.g. CoopApp
      // off-VPN). Both substrings appear in the same RUNTIME-003 error;
      // matching either is intentional defensive coverage. Do NOT broaden to
      // every "[ Federation Runtime ]" message — RUNTIME-001/002/etc. are
      // real config bugs and should not be reported as VPN issues.
      error.message.includes('Failed to get manifest') ||
      error.message.includes('#RUNTIME-003');

    let message: string;

    if (!isNetworkError) {
      message = `${error.name}: ${error.message}`;
    } else if (environment) {
      message = `It seems there is a connection issue in the ${environment} environment. Common issues may include: (1) VPN disconnection (2) misconfigured environment variables (3) Invalid x-coop-variant header or your PR stack hasn't deployed. Please verify and try again.`;
    } else {
      message =
        'A connection issue has occurred. Please check your network settings and try again.';
    }

    this.setState({ error: message });
  }

  render() {
    const { error } = this.state;

    if (error) {
      return this.props.fallback || <div>{error}</div>;
    }

    return <>{this.props.children}</>;
  }
}

export { ErrorBoundary };
